import { useEffect, useState } from 'react';
import {
  BODY_TYPES,
  BODY_TYPE_LABEL,
  CONTRACT_TYPES,
  CONTRACT_TYPE_HINT,
  CONTRACT_TYPE_LABEL,
  CURRENCIES,
  OVERRIDE_SCOPES,
  OVERRIDE_SCOPE_LABEL,
  totalMarkup,
  type AdminUserRow,
  type BodyType,
  type ContractLevelRow,
  type ContractMemberRow,
  type ContractOverrideRow,
  type ContractRow,
  type ContractType,
  type Currency,
  type OverrideScope,
} from 'shared';
import { api } from '../api';
import Slider from '../Slider';
import NumberField from '../NumberField';
import RegionPicker from '../RegionPicker';
import AdminList from '../AdminList';
import AdminFilters from '../AdminFilters';
import AdminSheet from '../AdminSheet';
import { alert, confirm } from '../telegram';

const EMPTY = {
  code: '',
  name: '',
  description: '',
  type: 'BUYER' as ContractType,
  region: '',
  levelId: '',
  coefficient: 1,
  isDeposit: false,
  comment: '',
  percent: 0,
  marketDegree: 0,
  usdt: 0,
  perWeight: 0,
  perWeightCurrency: 'USD' as Currency,
  perUnit: 0,
  perUnitCurrency: 'USD' as Currency,
};

const EMPTY_OVERRIDE = {
  scope: 'BODY_TYPE' as OverrideScope,
  bodyType: 'TENT' as BodyType,
  fromRegion: '',
  toRegion: '',
  partnerId: '',
  partnerLabel: '',
  percent: 0,
  perWeight: '',
  perUnit: '',
};

const overridesOf = (c: ContractRow) =>
  c.overridesCount.bodyType + c.overridesCount.route + c.overridesCount.partner;

/**
 * Сколько площадка возьмёт на самом деле.
 *
 * `totalPercent` с сервера — это только сумма трёх процентов; коэффициент
 * контракта `computeMarkup` применяет позже и ко всему удержанию сразу,
 * вместе с плоскими надбавками. В колонке «Наценка» стояла именно сумма,
 * и контракт с коэффициентом 1,2 показывал 2% там, где удерживает 2,4%.
 * Это столбец про деньги: он обязан называть то, что списывается.
 */
const effectivePercent = (m: { percent: number; marketDegree: number; usdt: number }, k: number) =>
  Math.round(totalMarkup(m) * k * 100) / 100;

/** «3% − 1% + 0%», а не «3% + -1% + 0%»: знак стоит в разрыве, а не при числе. */
function formula(m: { percent: number; marketDegree: number; usdt: number }): string {
  return [m.percent, m.usdt, m.marketDegree]
    .map((v, i) => (i === 0 ? `${v}%` : `${v < 0 ? '−' : '+'} ${Math.abs(v)}%`))
    .join(' ');
}

export default function AdminContracts() {
  const [tab, setTab] = useState<'contracts' | 'levels'>('contracts');
  const [contracts, setContracts] = useState<ContractRow[]>([]);
  const [levels, setLevels] = useState<ContractLevelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [fType, setFType] = useState('');
  const [fRegion, setFRegion] = useState('');
  const [fLevel, setFLevel] = useState('');
  /*
   * Поиск на клиенте: контрактов десятки, и просить сервер о фильтре
   * ради списка такого размера незачем. Остальные фильтры серверные —
   * они и так были.
   */
  const [query, setQuery] = useState('');

  /** Что разбираем: `new` — создаём, идентификатор — правим, `null` — закрыто. */
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState({ ...EMPTY });

  /**
   * Разделы разбора. Настройки, переопределения и участники — три
   * независимые стороны контракта, а не шаги одной формы: администратор
   * приходит за одной из них, а не за всеми сразу.
   */
  const [detailTab, setDetailTab] = useState<'settings' | 'overrides' | 'members'>('settings');
  const [members, setMembers] = useState<ContractMemberRow[]>([]);
  const [overrides, setOverrides] = useState<ContractOverrideRow[]>([]);
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<AdminUserRow[]>([]);
  const [ovForm, setOvForm] = useState({ ...EMPTY_OVERRIDE });
  const [showOv, setShowOv] = useState(false);

  const [newLevel, setNewLevel] = useState({ code: '', name: '', sortOrder: 0 });
  const [levelOpen, setLevelOpen] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([
        api.admin.contracts({
          type: fType || undefined,
          region: fRegion || undefined,
          levelId: fLevel || undefined,
        }),
        api.admin.levels(),
      ]);
      setContracts(c.items);
      setLevels(l.items);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fType, fRegion, fLevel]);

  useEffect(() => {
    if (!search.trim()) {
      setFound([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setFound((await api.admin.users({ q: search, limit: 12 })).items);
      } catch {
        setFound([]);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Итог считаем на лету тем же кодом, что и сервер при сделке.
  const total = totalMarkup(form);

  async function startEdit(c?: ContractRow) {
    setDetailTab('settings');
    setSearch('');
    setFound([]);
    setShowOv(false);
    setOvForm({ ...EMPTY_OVERRIDE });
    setMembers([]);
    setOverrides([]);

    if (!c) {
      setEditing('new');
      setForm({ ...EMPTY });
      return;
    }

    setEditing(c.id);
    setForm({
      code: c.code ?? '',
      name: c.name,
      description: c.description ?? '',
      type: c.type,
      region: c.region ?? '',
      levelId: c.levelId ?? '',
      coefficient: c.coefficient,
      isDeposit: c.isDeposit,
      comment: c.comment ?? '',
      percent: c.percent,
      marketDegree: c.marketDegree,
      usdt: c.usdt,
      perWeight: c.perWeight,
      perWeightCurrency: c.perWeightCurrency as Currency,
      perUnit: c.perUnit,
      perUnitCurrency: c.perUnitCurrency as Currency,
    });

    try {
      const [m, o] = await Promise.all([
        api.admin.contractMembers(c.id),
        api.admin.overrides(c.id),
      ]);
      setMembers(m.items);
      setOverrides(o.items);
    } catch (e) {
      alert((e as Error).message);
    }
  }

  async function save() {
    if (form.name.trim().length < 2) return alert('Укажите название контракта');

    const payload = {
      ...form,
      code: form.code || null,
      description: form.description || null,
      region: form.region || null,
      levelId: form.levelId || null,
      comment: form.comment || null,
      active: true,
    };

    setBusy(true);
    try {
      if (editing === 'new') await api.admin.createContract(payload);
      else if (editing) await api.admin.updateContract(editing, payload);
      setEditing(null);
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(c: ContractRow) {
    if (!(await confirm(`Удалить контракт «${c.name}»?`))) return;
    setBusy(true);
    try {
      await api.admin.deleteContract(c.id);
      setEditing(null);
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function addOverride() {
    if (!editing || editing === 'new') return;
    setBusy(true);
    try {
      await api.admin.addOverride(editing, {
        scope: ovForm.scope,
        bodyType: ovForm.scope === 'BODY_TYPE' ? ovForm.bodyType : null,
        fromRegion: ovForm.scope === 'ROUTE' ? ovForm.fromRegion || null : null,
        toRegion: ovForm.scope === 'ROUTE' ? ovForm.toRegion || null : null,
        partnerId: ovForm.scope === 'PARTNER' ? ovForm.partnerId || null : null,
        percent: ovForm.percent,
        perWeight: ovForm.perWeight ? Number(ovForm.perWeight) : null,
        perUnit: ovForm.perUnit ? Number(ovForm.perUnit) : null,
      });
      setOverrides((await api.admin.overrides(editing)).items);
      setOvForm({ ...EMPTY_OVERRIDE });
      setShowOv(false);
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function overrideTarget(o: ContractOverrideRow): string {
    if (o.scope === 'PARTNER') return o.partnerName ?? '—';
    if (o.scope === 'ROUTE') {
      return `${o.fromRegion ?? 'любой'} → ${o.toRegion ?? 'любой'}`;
    }
    return o.bodyType ? BODY_TYPE_LABEL[o.bodyType] : '—';
  }

  const current = contracts.find((c) => c.id === editing) ?? null;
  const level = levels.find((l) => l.id === levelOpen) ?? null;

  const q = query.trim().toLowerCase();
  const shown = q
    ? contracts.filter((c) =>
        [c.name, c.code ?? '', c.description ?? '']
          .join(' ')
          .toLowerCase()
          .includes(q),
      )
    : contracts;

  if (loading) return <div className="empty">Загрузка…</div>;

  return (
    <>
      {/* Взаимоисключающий переход между двумя разделами страницы —
          .seg, не .chips (те в этой системе — переключаемые фильтры). */}
      <div className="seg" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className={tab === 'contracts' ? 'seg__btn seg__btn--on' : 'seg__btn'}
          onClick={() => setTab('contracts')}
        >
          Контракты
        </button>
        <button
          type="button"
          className={tab === 'levels' ? 'seg__btn seg__btn--on' : 'seg__btn'}
          onClick={() => setTab('levels')}
        >
          Уровни контрактов
        </button>
      </div>

      {tab === 'levels' && (
        <>
          <div className="card">
            <h3>Новый уровень</h3>
            <div className="meta" style={{ marginBottom: 12 }}>
              Уровень решает, какой контракт применится, если участнику
              подходят несколько: берётся с наибольшим порядком.
            </div>
            <div className="field-row">
              <div className="field">
                <label>Код</label>
                <input
                  value={newLevel.code}
                  onChange={(e) => setNewLevel({ ...newLevel, code: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Порядок</label>
                <NumberField
                  decimal={false}
                  negative={false}
                  value={newLevel.sortOrder}
                  onChange={(v) => setNewLevel({ ...newLevel, sortOrder: v })}
                />
              </div>
            </div>
            <div className="field">
              <label>Название</label>
              <input
                value={newLevel.name}
                onChange={(e) => setNewLevel({ ...newLevel, name: e.target.value })}
              />
            </div>
            <button
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.admin.createLevel({ ...newLevel, active: true });
                  setNewLevel({ code: '', name: '', sortOrder: 0 });
                  await load();
                } catch (e) {
                  alert((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Добавить уровень
            </button>
          </div>

          <AdminList
            rows={levels}
            keyOf={(l) => l.id}
            empty="Уровней нет"
            action={{ label: 'Открыть', onClick: (l) => setLevelOpen(l.id) }}
            columns={[
              { title: 'Уровень', cell: (l) => <strong>{l.name}</strong> },
              { title: 'Код', width: '140px', labelOnPhone: true, inline: true, cell: (l) => l.code },
              {
                title: 'Порядок',
                width: '110px',
                labelOnPhone: true,
                inline: true,
                cell: (l) => <span className="tabular">{l.sortOrder}</span>,
              },
              {
                title: 'Контрактов',
                width: '120px',
                labelOnPhone: true,
                inline: true,
                cell: (l) => <span className="tabular">{l.contractsCount}</span>,
              },
            ]}
          />

          <AdminSheet
            open={level !== null}
            onClose={() => setLevelOpen(null)}
            title={level?.name ?? ''}
            subtitle={
              level && (
                <>
                  код {level.code} · порядок {level.sortOrder} · контрактов{' '}
                  {level.contractsCount}
                </>
              )
            }
            actions={
              level && (
                <>
                  <button className="secondary" onClick={() => setLevelOpen(null)}>
                    Закрыть
                  </button>
                  <button
                    className="danger"
                    disabled={busy}
                    onClick={async () => {
                      if (!(await confirm(`Удалить уровень «${level.name}»?`))) return;
                      setBusy(true);
                      try {
                        await api.admin.deleteLevel(level.id);
                        setLevelOpen(null);
                        await load();
                      } catch (e) {
                        alert((e as Error).message);
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Удалить
                  </button>
                </>
              )
            }
          >
            <div className="meta">
              Уровень решает, какой контракт применится, если участнику подходят
              несколько: берётся с наибольшим порядком, а при равенстве — тот, у
              которого указан регион.
            </div>
          </AdminSheet>
        </>
      )}

      {tab === 'contracts' && (
        <>
          <button className="anew" onClick={() => void startEdit()}>
            Добавить контракт
          </button>

          <AdminFilters
            active={[fType, fRegion, fLevel].filter(Boolean).length}
            search={
              <input
                placeholder="Название, код или описание"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            }
          >
            <div className="chips">
              {['', ...CONTRACT_TYPES].map((t) => (
                <button
                  key={t || 'all'}
                  type="button"
                  className={`chip ${fType === t ? 'on' : ''}`}
                  onClick={() => setFType(t)}
                >
                  {t ? CONTRACT_TYPE_LABEL[t as ContractType] : 'Все'}
                </button>
              ))}
            </div>
            <div className="field-row">
              <div className="field">
                <label>Локация</label>
                <RegionPicker
                  label="Локация"
                  value={fRegion}
                  onChange={setFRegion}
                  allowEmpty="Любая"
                />
              </div>
              <div className="field">
                <label>Уровень</label>
                <select value={fLevel} onChange={(e) => setFLevel(e.target.value)}>
                  <option value="">Любой</option>
                  {levels.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </AdminFilters>

          <AdminList
            rows={shown}
            keyOf={(c) => c.id}
            empty="Контрактов нет — площадка ничего не удерживает"
            // Выключенный контракт подсвечен: наценка по нему не берётся, и
            // вопрос «почему сделка прошла без удержания» чаще всего здесь.
            highlight={(c) => !c.active}
            action={{ label: 'Открыть', onClick: (c) => void startEdit(c) }}
            columns={[
              {
                /*
                 * Область (регион, уровень, депозитный) — метками под
                 * названием, а не своей колонкой: семи колонок хватало,
                 * чтобы имя контракта сжалось в столбик по букве.
                 */
                title: 'Контракт',
                width: 'minmax(220px, 1.4fr)',
                cell: (c) => (
                  <>
                    <strong>{c.name}</strong>
                    <span className="meta" style={{ display: 'block' }}>
                      {c.code && `${c.code} · `}
                      {c.description || '—'}
                    </span>
                    {(c.region || c.levelName || c.isDeposit) && (
                      <span style={{ display: 'block' }}>
                        {c.region && <span className="badge">{c.region}</span>}
                        {c.levelName && <span className="badge">{c.levelName}</span>}
                        {c.isDeposit && <span className="badge">депозитный</span>}
                      </span>
                    )}
                  </>
                ),
              },
              {
                title: 'Тип',
                width: '130px',
                cell: (c) => (
                  <span
                    className={`badge ${c.type === 'SELLER' ? 'badge--warn' : 'badge--verified'}`}
                  >
                    {CONTRACT_TYPE_LABEL[c.type]}
                  </span>
                ),
              },
              {
                title: 'Наценка',
                width: '150px',
                labelOnPhone: true,
                cell: (c) => (
                  <>
                    <span className="price tabular">{effectivePercent(c, c.coefficient)}%</span>
                    <span className="meta" style={{ display: 'block' }}>
                      {c.coefficient !== 1 ? `(${formula(c)}) × ${c.coefficient}` : formula(c)}
                    </span>
                  </>
                ),
              },
              {
                title: 'Надбавки',
                width: '130px',
                labelOnPhone: true,
                inline: true,
                cell: (c) => (
                  // Коэффициент множит и надбавки тоже — не назвать его
                  // здесь значило бы показать 300 там, где берут 360.
                  <span className="tabular">
                    {c.perWeight}/т · {c.perUnit}/рейс
                    {c.coefficient !== 1 && ` × ${c.coefficient}`}
                  </span>
                ),
              },
              {
                title: 'Участников',
                width: '110px',
                labelOnPhone: true,
                inline: true,
                cell: (c) => <span className="tabular">{c.membersCount}</span>,
              },
              {
                title: 'Переопределений',
                width: '140px',
                labelOnPhone: true,
                inline: true,
                cell: (c) => <span className="tabular">{overridesOf(c)}</span>,
              },
            ]}
          />
        </>
      )}

      <AdminSheet
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Новый контракт' : (current?.name ?? '')}
        subtitle={
          current && (
            <>
              {CONTRACT_TYPE_LABEL[current.type]} · итог{' '}
              {effectivePercent(current, current.coefficient)}% ·
              участников {current.membersCount}
              {!current.active && ' · выключен'}
            </>
          )
        }
        actions={
          <>
            {detailTab === 'settings' && (
              <button disabled={busy} onClick={() => void save()}>
                Сохранить
              </button>
            )}
            <button className="secondary" onClick={() => setEditing(null)}>
              {detailTab === 'settings' ? 'Отмена' : 'Закрыть'}
            </button>
            {current && (
              <button className="danger" disabled={busy} onClick={() => void remove(current)}>
                Удалить
              </button>
            )}
          </>
        }
      >
        {/* У нового контракта ни участников, ни переопределений ещё нет —
            переключать нечего, пока он не сохранён. */}
        {current && (
          <div className="seg" style={{ marginBottom: 'var(--s3)' }}>
            <button
              type="button"
              className={detailTab === 'settings' ? 'seg__btn seg__btn--on' : 'seg__btn'}
              onClick={() => setDetailTab('settings')}
            >
              Настройки
            </button>
            <button
              type="button"
              className={detailTab === 'overrides' ? 'seg__btn seg__btn--on' : 'seg__btn'}
              onClick={() => {
                setDetailTab('overrides');
                setSearch('');
                setFound([]);
              }}
            >
              Переопределения ({overrides.length})
            </button>
            <button
              type="button"
              className={detailTab === 'members' ? 'seg__btn seg__btn--on' : 'seg__btn'}
              onClick={() => {
                setDetailTab('members');
                setSearch('');
                setFound([]);
                // Иначе незаконченная форма переопределения с областью
                // «Контрагент» гасит поле поиска участника здесь же —
                // guard на ovForm.scope общий для обеих вкладок.
                setShowOv(false);
                setOvForm({ ...EMPTY_OVERRIDE });
              }}
            >
              Участники ({members.length})
            </button>
          </div>
        )}

        {(!current || detailTab === 'settings') && (
          <>
            <div className="field-row">
              <div className="field">
                <label>Код контракта</label>
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Название</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
            </div>

            <div className="field">
              <label>Описание</label>
              <input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <div className="field-row">
              <div className="field">
                <label>Тип</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value as ContractType })}
                >
                  {CONTRACT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {CONTRACT_TYPE_LABEL[t]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Локация</label>
                <RegionPicker
                  label="Локация"
                  value={form.region}
                  onChange={(v) => setForm({ ...form, region: v })}
                  allowEmpty="Любая"
                />
              </div>
            </div>

            <div className="meta" style={{ marginBottom: 12 }}>
              {CONTRACT_TYPE_HINT[form.type]}
            </div>

            <div className="field">
              <label>Уровень</label>
              <select
                value={form.levelId}
                onChange={(e) => setForm({ ...form, levelId: e.target.value })}
              >
                <option value="">Без уровня</option>
                {levels.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="field-row">
              <div className="field">
                <label>Коэффициент</label>
                <NumberField
                  negative={false}
                  value={form.coefficient}
                  onChange={(v) => setForm({ ...form, coefficient: v })}
                />
              </div>
              <div className="field">
                <label>Депозитный</label>
                <button
                  type="button"
                  className={`chip ${form.isDeposit ? 'on' : ''}`}
                  onClick={() => setForm({ ...form, isDeposit: !form.isDeposit })}
                >
                  {form.isDeposit ? 'да' : 'нет'}
                </button>
              </div>
            </div>

            <div className="field">
              <label>Комментарий</label>
              <input
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
              />
            </div>

            <div className="divider" />
            <h3>Наценки</h3>

            <div className="field">
              <label>Наценка, %</label>
              <NumberField
                value={form.percent}
                onChange={(v) => setForm({ ...form, percent: v })}
              />
            </div>

            <Slider
              label="Рыночная степень"
              min={-1}
              max={3}
              step={0.5}
              value={form.marketDegree}
              onChange={(v) => setForm({ ...form, marketDegree: v })}
            />

            <Slider
              label="USDT"
              min={-2}
              max={2}
              step={0.5}
              value={form.usdt}
              onChange={(v) => setForm({ ...form, usdt: v })}
            />

            <div className="card card--inset">
              <strong>
                Итоговая наценка: {formula(form)} = {total}%
                {form.coefficient !== 1 &&
                  ` · с коэффициентом ${form.coefficient} — ${effectivePercent(form, form.coefficient)}%`}
              </strong>
            </div>

            <div className="divider" />
            <h3>Надбавки</h3>

            <div className="field-row">
              <div className="field">
                <label>За тонну</label>
                <NumberField
                  value={form.perWeight}
                  onChange={(v) => setForm({ ...form, perWeight: v })}
                />
              </div>
              <div className="field">
                <label>Валюта</label>
                <select
                  value={form.perWeightCurrency}
                  onChange={(e) =>
                    setForm({ ...form, perWeightCurrency: e.target.value as Currency })
                  }
                >
                  {CURRENCIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field-row">
              <div className="field">
                <label>За рейс</label>
                <NumberField
                  value={form.perUnit}
                  onChange={(v) => setForm({ ...form, perUnit: v })}
                />
              </div>
              <div className="field">
                <label>Валюта</label>
                <select
                  value={form.perUnitCurrency}
                  onChange={(e) =>
                    setForm({ ...form, perUnitCurrency: e.target.value as Currency })
                  }
                >
                  {CURRENCIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {current && detailTab === 'overrides' && (
          <>
            <div className="meta" style={{ marginBottom: 12 }}>
              Меняют только процент. Рыночная степень и USDT остаются от
              контракта. Чем уже область, тем выше приоритет: контрагент важнее
              направления, направление — типа кузова.
            </div>

            {overrides.length === 0 && <div className="meta">Переопределений нет.</div>}

            {overrides.map((o) => (
              <div key={o.id} className="row" style={{ marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div>
                    <span className="badge">{OVERRIDE_SCOPE_LABEL[o.scope]}</span>{' '}
                    {overrideTarget(o)}
                  </div>
                  <div className="meta">
                    {o.percent}% → итог{' '}
                    {effectivePercent(
                      { percent: o.percent, marketDegree: current.marketDegree, usdt: current.usdt },
                      current.coefficient,
                    )}
                    %{o.perWeight !== null && ` · ${o.perWeight}/т`}
                    {o.perUnit !== null && ` · ${o.perUnit}/рейс`}
                    {current.coefficient !== 1 && ` · с коэффициентом ${current.coefficient}`}
                  </div>
                </div>
                <button
                  className="danger small"
                  disabled={busy}
                  onClick={async () => {
                    await api.admin.removeOverride(current.id, o.id);
                    setOverrides((await api.admin.overrides(current.id)).items);
                    await load();
                  }}
                >
                  убрать
                </button>
              </div>
            ))}

            {!showOv ? (
              <button
                className="secondary"
                style={{ marginTop: 8 }}
                onClick={() => setShowOv(true)}
              >
                Добавить переопределение
              </button>
            ) : (
              <div style={{ marginTop: 12 }}>
                <div className="field">
                  <label>Область</label>
                  <select
                    value={ovForm.scope}
                    onChange={(e) =>
                      setOvForm({ ...ovForm, scope: e.target.value as OverrideScope })
                    }
                  >
                    {OVERRIDE_SCOPES.map((s) => (
                      <option key={s} value={s}>
                        {OVERRIDE_SCOPE_LABEL[s]}
                      </option>
                    ))}
                  </select>
                </div>

                {ovForm.scope === 'BODY_TYPE' && (
                  <div className="field">
                    <label>Тип кузова</label>
                    <select
                      value={ovForm.bodyType}
                      onChange={(e) =>
                        setOvForm({ ...ovForm, bodyType: e.target.value as BodyType })
                      }
                    >
                      {BODY_TYPES.map((b) => (
                        <option key={b} value={b}>
                          {BODY_TYPE_LABEL[b]}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {ovForm.scope === 'ROUTE' && (
                  <div className="field-row">
                    <div className="field">
                      <label>Откуда</label>
                      <RegionPicker
                        label="Откуда"
                        value={ovForm.fromRegion}
                        onChange={(v) => setOvForm({ ...ovForm, fromRegion: v })}
                        allowEmpty="Любой"
                      />
                    </div>
                    <div className="field">
                      <label>Куда</label>
                      <RegionPicker
                        label="Куда"
                        value={ovForm.toRegion}
                        onChange={(v) => setOvForm({ ...ovForm, toRegion: v })}
                        allowEmpty="Любой"
                      />
                    </div>
                  </div>
                )}

                {ovForm.scope === 'PARTNER' && (
                  <div className="field">
                    <label>Контрагент</label>
                    <input
                      placeholder="Поиск по логину или имени"
                      value={ovForm.partnerLabel || search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setOvForm({ ...ovForm, partnerId: '', partnerLabel: '' });
                      }}
                    />
                    {found.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        className="chip"
                        style={{ marginTop: 8 }}
                        onClick={() => {
                          setOvForm({
                            ...ovForm,
                            partnerId: u.id,
                            partnerLabel:
                              [u.firstName, u.lastName].filter(Boolean).join(' ') ||
                              u.login ||
                              '',
                          });
                          setSearch('');
                          setFound([]);
                        }}
                      >
                        {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.login}
                      </button>
                    ))}
                  </div>
                )}

                <div className="field-row">
                  <div className="field">
                    <label>Наценка, %</label>
                    <NumberField
                      value={ovForm.percent}
                      onChange={(v) => setOvForm({ ...ovForm, percent: v })}
                    />
                  </div>
                  <div className="field">
                    <label>За тонну</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="как в контракте"
                      value={ovForm.perWeight}
                      onChange={(e) => setOvForm({ ...ovForm, perWeight: e.target.value })}
                    />
                  </div>
                  <div className="field">
                    <label>За рейс</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="как в контракте"
                      value={ovForm.perUnit}
                      onChange={(e) => setOvForm({ ...ovForm, perUnit: e.target.value })}
                    />
                  </div>
                </div>

                <div className="meta" style={{ marginBottom: 8 }}>
                  Итог:{' '}
                  {formula({
                    percent: ovForm.percent,
                    marketDegree: current.marketDegree,
                    usdt: current.usdt,
                  })}{' '}
                  ={' '}
                  {effectivePercent(
                    {
                      percent: ovForm.percent,
                      marketDegree: current.marketDegree,
                      usdt: current.usdt,
                    },
                    current.coefficient,
                  )}
                  %{current.coefficient !== 1 && ` (с коэффициентом ${current.coefficient})`}
                </div>

                <div className="col">
                  <button disabled={busy} onClick={() => void addOverride()}>
                    Добавить
                  </button>
                  <button className="secondary" onClick={() => setShowOv(false)}>
                    Отмена
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {current && detailTab === 'members' && (
          <>
            {ovForm.scope !== 'PARTNER' && (
              <div className="field">
                <input
                  placeholder="Добавить: поиск по логину, имени, телефону"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}

            {ovForm.scope !== 'PARTNER' &&
              found.map((u) => (
                <div key={u.id} className="row" style={{ marginBottom: 8 }}>
                  <div className="meta" style={{ flex: 1 }}>
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.login}
                  </div>
                  <button
                    className="small"
                    disabled={busy || members.some((m) => m.userId === u.id)}
                    onClick={async () => {
                      await api.admin.addContractMembers(current.id, [u.id]);
                      setMembers((await api.admin.contractMembers(current.id)).items);
                      setSearch('');
                      await load();
                    }}
                  >
                    {members.some((m) => m.userId === u.id) ? 'уже в списке' : 'Добавить'}
                  </button>
                </div>
              ))}

            {members.length === 0 && <div className="meta">Участников нет.</div>}

            {members.map((m) => (
              <div key={m.id} className="row" style={{ marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div>{m.name}</div>
                  <div className="meta">
                    {m.login ? `@${m.login}` : m.phone || '—'}
                    {m.banned && ' · в бане'}
                  </div>
                </div>
                <button
                  className="danger small"
                  disabled={busy}
                  onClick={async () => {
                    await api.admin.removeContractMember(current.id, m.userId);
                    setMembers((ms) => ms.filter((x) => x.userId !== m.userId));
                    await load();
                  }}
                >
                  убрать
                </button>
              </div>
            ))}
          </>
        )}
      </AdminSheet>
    </>
  );
}
